import type { ReactNode } from 'react';
import { memo } from 'react';
import TableBody from '../../../../core/components/table/TableBody';
import TableHead from '../../../../core/components/table/TableHead';
import type { BaseProductData } from '../../../types';
import Table from '../../../../core/components/table/Table';
import type { ProductBackorder } from '../../../queries/useProductBackorders';
import ProductVariantsTableHeadRow from './ProductVariantsTableHeadRow';
import ProductVariantsTableProduct from './ProductVariantsTableProduct';

const ProductVariantsTable = ({
    hideImageColumn = false,
    fixedWidth = false,
    showAvailabilityMessage = false,
    emphasizeSku = false,
    products,
    shouldShowSiblingsColumn,
    shouldShowVariantsColumn,
    actionsColumn,
    showDiscountColumn,
    backorders,
}: {
    hideImageColumn?: boolean;
    fixedWidth?: boolean;
    showAvailabilityMessage?: boolean;
    emphasizeSku?: boolean;
    products: BaseProductData[];
    shouldShowSiblingsColumn: boolean;
    shouldShowVariantsColumn: boolean;
    actionsColumn?(sku: string): ReactNode;
    showDiscountColumn?: boolean;
    backorders?: Record<string, ProductBackorder[]>;
}) => {
    if (products.length === 0) {
        return <div />;
    }

    return (
        <div data-test-class="productVariantsTable">
            <Table striped>
                <TableHead>
                    <ProductVariantsTableHeadRow
                        hideImageColumn={hideImageColumn}
                        siblingHeaders={products[0].siblingAttributes?.map(
                            ({ name }) => name
                        )}
                        variantHeaders={products[0].variants[0]?.variationAttributes?.map(
                            ({ name }) => name
                        )}
                        hasSiblings={shouldShowSiblingsColumn}
                        hasVariants={shouldShowVariantsColumn}
                        showDiscountColumn={showDiscountColumn}
                        showBackordersColumn={!!backorders}
                    />
                </TableHead>
                <TableBody>
                    {products.map((product, index) => {
                        return (
                            <ProductVariantsTableProduct
                                fixedWidth={fixedWidth}
                                showAvailabilityMessage={
                                    showAvailabilityMessage
                                }
                                hideImageColumn={hideImageColumn}
                                isLast={index === products.length - 1}
                                key={product.id}
                                product={product}
                                shouldShowSiblingsColumn={
                                    shouldShowSiblingsColumn
                                }
                                shouldShowVariantsColumn={
                                    shouldShowVariantsColumn
                                }
                                showDiscountColumn={showDiscountColumn}
                                backorders={backorders}
                                actionsColumn={actionsColumn}
                                emphasizeSku={emphasizeSku}
                            />
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

export default memo(ProductVariantsTable);
